import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { RevenueDetails } from '../marketing/RevenueDetails'
import RevenueBarChart from './RevenueBarChart'
import { StatsCards } from './StatsCards'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { closeModal, showModal } from 'app/lib/modal'
import type { RevenueDailyMetric, RevenueStatsType } from 'types/analytics'
import { StatsCardType } from 'types/StatsCardType'

type GeneralRevenueSectionProps = {
  metrics?: RevenueStatsType
  fromDate: string
  toDate: string
  locationIds: string[]
  dailyMetrics: RevenueDailyMetric[]
  isLoading: boolean
}

function useGeneralRevenueSection({
  metrics,
  fromDate,
  toDate,
  locationIds,
}: Pick<GeneralRevenueSectionProps, `metrics` | `fromDate` | `toDate` | `locationIds`>) {
  const showBarChart = useFeatureToggle(`revenue-dashboard`)
  const [stats, setStats] = useState<StatsCardType[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterContactsBy, setFilterContactsBy] = useState(searchParams.get(`details`))

  useEffect(() => {
    if (metrics) {
      // Dividing by the gross average recovers the visit count, so this line
      // shares a denominator with Gross and Net Production (TRK-5840).
      const visits = metrics.avgGrossProductionPerVisit
        ? metrics.grossProduction / metrics.avgGrossProductionPerVisit
        : 0
      const avgCollectionsPerVisit = visits ? (metrics.collections ?? 0) / visits : 0
      setStats([
        {
          label: `Gross Production`,
          value: `${metrics.grossProduction.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          bottomStatLabel: `Average gross production per visit`,
          bottomStatValue: `${metrics.avgGrossProductionPerVisit.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          trend: undefined,
          infoTooltip: {
            title: `Gross Production`,
            message: `Sum of procedure transactions in the date range.`,
          },
          onOpenClick: () => openModal(`Gross Production`),
        },
        {
          label: `Adjustments`,
          value: `${metrics.adjustments.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          bottomStatLabel: `Average adjustment per visit`,
          bottomStatValue: `${metrics.avgAdjustmentsPerVisit.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          trend: undefined,
          infoTooltip: {
            title: `Adjustments`,
            message: `Sum of adjustment transactions in the date range.`,
          },
          onOpenClick: () => openModal(`Adjustments`),
        },
        {
          label: `Net Production`,
          value: `${metrics.netProduction.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          bottomStatLabel: `Average net production per visit`,
          bottomStatValue: `${metrics.avgNetProductionPerVisit.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          trend: undefined,
          infoTooltip: {
            title: `Net Production`,
            message: `The value of gross production minus the value of adjustments.`,
          },
          onOpenClick: () => openModal(`Net Production`),
        },
        {
          label: `Collections`,
          // Reads defensively: an older cached response shape has no
          // collections and must render as zero rather than throw.
          value: `${(metrics.collections ?? 0).toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          bottomStatLabel: `Average collection per visit`,
          bottomStatValue: `${avgCollectionsPerVisit.toLocaleString(`en-US`, {
            style: `currency`,
            currency: `USD`,
            maximumFractionDigits: 0,
          })}`,
          trend: undefined,
          infoTooltip: {
            title: `Collections`,
            message: `Sum of payment transactions in the date range.`,
          },
        },
      ])
    }
  }, [metrics])

  useEffect(() => {
    let currentParam = searchParams.get(`details`)
    if (filterContactsBy === currentParam) return
    if (filterContactsBy != null) {
      setSearchParams((prev) => {
        prev.set(`details`, filterContactsBy)
        return prev
      })
    } else {
      setSearchParams((prev) => {
        prev.delete(`details`)
        return prev
      })
    }
  }, [filterContactsBy])

  useEffect(() => {
    if (
      filterContactsBy &&
      [`Gross Production`, `Adjustments`, `Net Production`].includes(filterContactsBy) &&
      fromDate &&
      toDate
    ) {
      let filterBy: `production` | `adjustments` | `all` =
        filterContactsBy === `Gross Production` || filterContactsBy === `Net Production`
          ? `production`
          : filterContactsBy === `Adjustments`
            ? `adjustments`
            : `all`
      const handleClose = () => {
        closeModal()
        setFilterContactsBy(null)
      }
      showModal(
        <RevenueDetails
          filterTransactionsBy={filterBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          title={filterContactsBy}
          onClose={handleClose}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: handleClose,
          useRadix: true,
        },
      )
    }
  }, [filterContactsBy, fromDate, toDate])

  const openModal = (type: `Gross Production` | `Adjustments` | `Net Production`) => {
    setFilterContactsBy(type)
  }

  return {
    showBarChart,
    stats,
  }
}

type GeneralRevenueSectionComponentProps = ReturnType<typeof useGeneralRevenueSection> & GeneralRevenueSectionProps

export function GeneralRevenueSectionComponent(props: GeneralRevenueSectionComponentProps) {
  const { stats, isLoading, showBarChart, dailyMetrics } = props

  return (
    <>
      <div className="mx-auto w-full">
        <StatsCards stats={stats} />
        {showBarChart && <RevenueBarChart dailyMetrics={dailyMetrics} isLoading={isLoading} />}
      </div>
    </>
  )
}

// observer: useGeneralRevenueSection reads the revenue-dashboard feature toggle, which
// resolves false -> real asynchronously; the component that calls the hook must be an
// observer so the bar chart appears without a manual refresh (TRK-6020).
const GeneralRevenueSection = observer(function GeneralRevenueSection(props: GeneralRevenueSectionProps) {
  const { metrics, fromDate, toDate, locationIds } = props
  const revenueData = useGeneralRevenueSection({ metrics, fromDate, toDate, locationIds })

  return <GeneralRevenueSectionComponent {...revenueData} {...props} />
})

export default GeneralRevenueSection
